'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './CTACopyContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const steps = [
  {
    number: '1',
    title: 'Enter your product details',
    description:
      'Input your product or service name and a short value proposition that describes the main benefit you deliver. These details personalize every CTA to your brand.',
  },
  {
    number: '2',
    title: 'Choose your action, tone, and industry',
    description:
      'Select the target action (Sign Up, Buy Now, Download, etc.), your preferred tone (Professional, Urgent, Playful, etc.), and your industry. These three inputs shape the style and language of your CTAs.',
  },
  {
    number: '3',
    title: 'Get 13 ready-to-use CTA variations',
    description:
      'Instantly receive 5 button texts, 3 headlines, 3 email subject lines, and 2 ad copy snippets. Copy any variation with one click. No sign-up required. Completely free.',
  },
];

const ctaPerformanceData = [
  { type: 'Button CTA', avgCTR: '3 - 5%', color: '#166534', bg: '#dcfce7', notes: 'Direct and visible. Works best above the fold with clear, specific copy.' },
  { type: 'Banner CTA', avgCTR: '0.5 - 1.5%', color: '#92400e', bg: '#fef3c7', notes: 'Often ignored due to banner blindness. Requires strong visual contrast.' },
  { type: 'Pop-up CTA', avgCTR: '3 - 9%', color: '#166534', bg: '#dcfce7', notes: 'Highest engagement when triggered by exit intent or scroll depth.' },
  { type: 'Inline Text CTA', avgCTR: '1 - 3%', color: '#1e40af', bg: '#dbeafe', notes: 'Subtle but effective in blog posts and long-form content.' },
  { type: 'Sidebar CTA', avgCTR: '0.5 - 1.5%', color: '#92400e', bg: '#fef3c7', notes: 'Low visibility on mobile. Best for desktop-heavy audiences.' },
  { type: 'Slide-in CTA', avgCTR: '2 - 5%', color: '#5b21b6', bg: '#ede9fe', notes: 'Less intrusive than popups. Good for secondary offers and lead magnets.' },
  { type: 'Sticky Bar CTA', avgCTR: '1 - 3%', color: '#1e40af', bg: '#dbeafe', notes: 'Always visible. Works well for sitewide promotions and announcements.' },
  { type: 'Email CTA', avgCTR: '2 - 5%', color: '#5b21b6', bg: '#ede9fe', notes: 'Performance depends heavily on subject line and email list quality.' },
];

const ctaByIndustryData = [
  { industry: 'SaaS', topActions: 'Start Free Trial, Book Demo', avgCTR: '3 - 7%', notes: 'Free trial CTAs outperform demo requests by 2x on average' },
  { industry: 'E-Commerce', topActions: 'Buy Now, Add to Cart', avgCTR: '2 - 5%', notes: 'Urgency-driven CTAs with countdown timers boost conversions 25%' },
  { industry: 'Agency', topActions: 'Get Quote, Book Call', avgCTR: '2 - 4%', notes: 'Personalized CTAs convert 3x better than generic ones' },
  { industry: 'Education', topActions: 'Enroll Now, Download', avgCTR: '3 - 6%', notes: 'Free resource downloads generate the highest lead volume' },
  { industry: 'Healthcare', topActions: 'Book Appointment, Learn More', avgCTR: '2 - 4%', notes: 'Trust-building language increases CTA engagement 40%' },
  { industry: 'Finance', topActions: 'Get Quote, Start Free', avgCTR: '1 - 3%', notes: 'Security-focused copy lifts click-through rates significantly' },
];

const ctaMistakes = [
  {
    icon: '🔤',
    title: 'Vague or generic copy',
    description:
      'CTAs like &quot;Click Here&quot; or &quot;Submit&quot; tell visitors nothing about what they will get. Generic copy blends into the page and fails to create any motivation to act. Every CTA should answer the question: &quot;What is in it for me?&quot;',
    stat: 'Specific CTAs outperform generic ones by 42%',
  },
  {
    icon: '🎨',
    title: 'Poor visual contrast',
    description:
      'A CTA button that blends into the background is invisible to visitors. If your button color, size, or placement does not stand out from surrounding content, people scroll right past it without noticing.',
    stat: 'High-contrast CTAs increase clicks by 30%',
  },
  {
    icon: '📢',
    title: 'Too many CTAs competing',
    description:
      'When every section has a different call to action, visitors get overwhelmed and choose none of them. One primary CTA per page with a clear hierarchy converts far better than scattering five different asks across the same view.',
    stat: 'Single-CTA pages convert 13.5% more than multi-CTA pages',
  },
  {
    icon: '⏳',
    title: 'No sense of urgency',
    description:
      'Without urgency or scarcity, visitors bookmark the page and never come back. Adding a deadline, limited availability, or time-sensitive benefit gives people a reason to act now instead of later.',
    stat: 'Urgency-driven CTAs boost conversions by 25-35%',
  },
  {
    icon: '📱',
    title: 'Not optimized for mobile',
    description:
      'CTA buttons that are too small to tap, positioned below the fold on mobile, or surrounded by too much text get ignored on phones. More than half of traffic is mobile, and your CTAs must work perfectly on small screens.',
    stat: 'Mobile-optimized CTAs see 20% higher tap rates',
  },
  {
    icon: '🧪',
    title: 'Never testing variations',
    description:
      'Running the same CTA copy for months without testing alternatives means you are leaving conversions on the table. Small changes to wording, color, placement, or size can produce dramatic improvements when tested methodically.',
    stat: 'A/B testing CTAs lifts conversion rates by 20-30%',
  },
];

const tips = [
  {
    number: '01',
    title: 'Use action verbs that match the outcome',
    description:
      'Start every CTA with a strong verb that describes what the visitor will get, not just what they will do. &quot;Get Your Free Report&quot; outperforms &quot;Submit&quot; because it frames the action as a benefit. Match the verb to the outcome the user cares about.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '02',
    title: 'Add CTAs to popups for maximum visibility',
    description:
      'A well-timed popup puts your CTA front and center when visitors are most engaged. Exit-intent popups recover bouncing traffic, scroll-triggered popups catch engaged readers, and timed popups capture attention at the right moment.',
    cta: 'Try Popup Builder widget',
    ctaHref: '/widgets/popup-builder',
  },
  {
    number: '03',
    title: 'Create urgency with countdown timers',
    description:
      'Pairing your CTA with a visible countdown timer increases urgency and drives faster action. Limited-time offers, sale endings, and event deadlines all benefit from a ticking clock that reminds visitors the opportunity will not last forever.',
    cta: 'Try Countdown widget',
    ctaHref: '/widgets/countdown',
  },
  {
    number: '04',
    title: 'Place CTAs where attention is highest',
    description:
      'Above the fold, after key benefit statements, at the end of content sections, and in sticky headers. Do not bury your CTA at the bottom of a long page. Place it where visitors are most likely to be convinced and ready to act.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '05',
    title: 'Use social proof near your CTAs',
    description:
      'Placing testimonials, review counts, or trust badges directly next to your CTA button builds confidence at the moment of decision. Visitors who see that others have already taken the action are more likely to follow.',
    cta: 'Try Testimonials widget',
    ctaHref: '/widgets/testimonials',
  },
  {
    number: '06',
    title: 'Keep button text under 5 words',
    description:
      'Short CTA text is easier to scan, faster to understand, and more likely to be clicked. &quot;Start Free Trial&quot; beats &quot;Click Here to Start Your Free Trial Today&quot; every time. Cut every word that does not add meaning.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '07',
    title: 'Gamify your CTAs with spin wheels',
    description:
      'Replace a static &quot;Sign Up&quot; button with a spin-the-wheel offer. Visitors enter their email to spin for a discount, free resource, or bonus. Gamification turns a boring form into an interactive experience and lifts opt-in rates by 30% or more.',
    cta: 'Try Spinning Wheel widget',
    ctaHref: '/widgets/spinning-wheel',
  },
  {
    number: '08',
    title: 'Test one variable at a time',
    description:
      'Change only the CTA copy, or only the button color, or only the placement in each test. Testing multiple variables simultaneously makes it impossible to know what caused the improvement. Systematic testing compounds small wins into big results.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'CTR (Click-Through Rate)',
    definition: 'The percentage of people who click on your CTA out of all who see it. The primary metric for measuring CTA effectiveness.',
    formula: '(Clicks / Impressions) x 100',
    whenToUse: 'Evaluating how compelling your CTA copy and design are',
  },
  {
    term: 'Conversion Rate',
    definition: 'The percentage of visitors who complete the desired action after clicking the CTA. Measures the full journey from click to completion.',
    formula: '(Conversions / Total Visitors) x 100',
    whenToUse: 'Measuring end-to-end effectiveness of your CTA and landing page',
  },
  {
    term: 'Click-Through',
    definition: 'The raw number of clicks your CTA receives. Useful for volume comparisons but should always be paired with rate metrics for context.',
    formula: 'Total Clicks on CTA',
    whenToUse: 'Comparing traffic volume driven by different CTA placements',
  },
  {
    term: 'Bounce Rate',
    definition: 'The percentage of visitors who leave your page without interacting with any CTA or content. A high bounce rate suggests your CTA is not visible or compelling enough.',
    formula: '(Single-Page Sessions / Total Sessions) x 100',
    whenToUse: 'Diagnosing whether your CTA placement and copy are engaging visitors',
  },
  {
    term: 'Micro-Conversion',
    definition: 'A smaller action that indicates progress toward a primary conversion. Examples include email signups, resource downloads, and video plays.',
    formula: 'Defined by conversion funnel stages',
    whenToUse: 'Tracking incremental engagement and funnel progression',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface IProps {
  relatedPosts: IRelatedPost[];
}

export const CTACopyContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      {/* ── How It Works ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free CTA copy generator</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Enter your product details
            and preferences to instantly generate 13 CTA variations across 4 categories.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── CTA Copywriting Principles ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>CTA Copywriting Principles</StyledSectionLabel>
          <StyledSectionHeading>
            The AIDA framework for high-converting CTAs
          </StyledSectionHeading>
          <StyledSectionIntro>
            This free CTA generator uses proven copywriting principles to create CTAs that
            drive action. Here is the framework behind every variation.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">AIDA Framework</p>
            <p className="formula">Attention → Interest → Desire → Action</p>
            <p className="example">
              Example: <span>&quot;Stop Losing Leads&quot; (Attention) → &quot;See How {'{'}Product{'}'} Converts 3x More&quot; (Interest) → &quot;Join 10,000+ Teams&quot; (Desire) → &quot;Start Free Trial&quot; (Action)</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              Every high-converting CTA follows the AIDA framework, even if the writer does not
              realize it. Attention grabs the visitor with a bold claim or question. Interest
              builds curiosity with a specific benefit. Desire creates emotional motivation by
              showing proof or scarcity. Action tells the visitor exactly what to do next.
            </p>
            <p>
              Button CTAs typically handle the Action step: short, specific, and verb-driven.
              Headlines cover Attention and Interest. Email subject lines need to nail Attention
              in under 60 characters. Ad copy compresses all four steps into a single paragraph.
            </p>
            <p>
              The best CTAs also reduce friction. Words like &quot;free,&quot; &quot;no credit card,&quot; and
              &quot;instant&quot; lower the perceived risk of clicking. The easier you make it to say yes,
              the more people will.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      {/* ── CTA Performance by Type ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>CTA Performance by Type</StyledSectionLabel>
          <StyledSectionHeading>
            Average click-through rates by CTA type in 2026
          </StyledSectionHeading>
          <StyledSectionIntro>
            Not all CTA formats perform equally. Understanding which types drive the highest
            engagement helps you choose where to place your best copy.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>CTA Type</th>
                  <th>Average CTR</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {ctaPerformanceData.map((row) => (
                  <tr key={row.type}>
                    <td className="col-name">{row.type}</td>
                    <td>
                      <StyledBadge color={row.color} bg={row.bg}>
                        {row.avgCTR}
                      </StyledBadge>
                    </td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Sources: HubSpot, Sumo, OptinMonster, 2026/2027 averages.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* ── CTA by Industry ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>CTA by Industry</StyledSectionLabel>
          <StyledSectionHeading>
            Top-performing CTA actions by industry in 2026
          </StyledSectionHeading>
          <StyledSectionIntro>
            Different industries respond to different CTA actions and tones. Use this table
            to see which approaches work best in your vertical.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Industry</th>
                  <th>Top Actions</th>
                  <th>Avg CTR</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {ctaByIndustryData.map((row) => (
                  <tr key={row.industry}>
                    <td className="col-name">{row.industry}</td>
                    <td className="col-value">{row.topActions}</td>
                    <td className="col-value">{row.avgCTR}</td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Sources: HubSpot, Unbounce, WordStream, 2026/2027 averages.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* ── Common CTA Mistakes ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Common CTA Mistakes</StyledSectionLabel>
          <StyledSectionHeading>
            Six mistakes that kill your CTA performance
          </StyledSectionHeading>
          <StyledSectionIntro>
            Great CTA copy is only half the battle. These common mistakes silently undermine
            even the best-written calls to action and leave conversions on the table.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {ctaMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p dangerouslySetInnerHTML={{ __html: factor.description }} />
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── 8 Tips for Better CTAs ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Improve Your CTAs</StyledSectionLabel>
          <StyledSectionHeading>
            8 proven tips for higher-converting CTAs
          </StyledSectionHeading>
          <StyledSectionIntro>
            These strategies help you turn great CTA copy into real clicks and conversions.
            All CommonNinja widgets mentioned below are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p dangerouslySetInnerHTML={{ __html: tip.description }} />
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} →
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── CTA Metrics Glossary ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>CTA Metrics Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            CTA performance metrics compared
          </StyledSectionHeading>
          <StyledSectionIntro>
            Different CTA metrics answer different questions about your conversion funnel
            performance. Here is how they compare and when to use each one.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Definition</th>
                  <th>Formula</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {/* ── Related Articles ── */}
      {relatedPosts.length > 0 && (
        <StyledSection bg="var(--gray-50)">
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on CTA optimization and copywriting</StyledSectionHeading>
            <StyledSectionIntro>
              Dig deeper into the strategies behind writing high-converting CTAs and
              building a more effective conversion funnel.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                >
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article →</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
